/**
 * Disclosure — a titled row that opens to reveal supporting content (the listing preview's readiness
 * panel). Not for form fields: those stay visible. Content is unmounted while closed.
 */

import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  title: string;
  /** Muted text beside the title (e.g. "3 of 15"). */
  meta?: string;
  /** Prefix for data-testid values: `${testId}-toggle`, `${testId}-panel`. */
  testId: string;
  headingLevel?: 2 | 3 | 4;
  children: ReactNode;
}

export function Disclosure({ title, meta, testId, headingLevel = 3, children }: Props) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div data-testid={testId}>
      <Heading className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((value) => !value)}
          data-testid={`${testId}-toggle`}
          className="flex w-full min-h-[44px] items-center justify-between gap-3 rounded-md py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0">
            <span className="text-sm font-semibold text-foreground">{title}</span>
            {meta && <span className="ml-2 text-xs font-normal text-muted-foreground">{meta}</span>}
          </span>
          <Chevron aria-hidden className={cn('h-4 w-4 shrink-0 text-muted-foreground')} />
        </button>
      </Heading>
      {open && (
        <div id={panelId} data-testid={`${testId}-panel`} className="pb-4 pt-1">
          {children}
        </div>
      )}
    </div>
  );
}
